import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MultiSelectModule } from 'primeng/multiselect';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';
import { EStorageKeys } from '../../shared/enums/storage-keys.enum';
import { DeleteConfirmationComponent } from '../../shared/modals/delete-confirmation/delete-confirmation.component';
import { DeliveryNoteOverviewModalComponent } from '../../shared/modals/delivery-note-overview-modal/delivery-note-overview-modal.component';
import { DeliveryStatusEnum, IDeliveryNote, ShippingTypeEnum } from '../../shared/models/delivery-note.model';
import { DeliveryNoteService } from '../../shared/services/delivery-note.service';
import { FilterStorageService } from '../../shared/services/filter-storage.service';
import { HelperService } from '../../shared/services/helper.service';
import { ModalService } from '../../shared/services/modal.service';
import { SocketService } from '../../shared/services/socket.service';
import { ToastService } from '../../shared/services/toast.service';

@Component({
  selector: 'app-delivery-notes',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent, DeliveryNoteOverviewModalComponent, MultiSelectModule],
  templateUrl: './delivery-notes.component.html',
  styleUrls: ['./delivery-notes.component.scss'],
})
export class DeliveryNotesComponent implements OnInit, OnDestroy {
  public deliveryNotes: IDeliveryNote[] = [];
  public isLoading = false;
  public showOverviewModal = false;
  public selectedDeliveryNote: IDeliveryNote | null = null;
  public selectedOrderInfo = '';
  public searchTerm = '';
  public searchTerms = new Subject<string>();

  public currentPage = 1;
  public pageSize = 10;
  public totalItems = 0;
  public totalPages = 1;
  public sortField = 'createdAt';
  public sortDirection = 'desc';

  public statusFilter: DeliveryStatusEnum[] = [];
  public DeliveryStatusEnum = DeliveryStatusEnum;
  public statusOptions = [
    { value: DeliveryStatusEnum.DELIVERYNOTEQSCREATED, label: 'QS - Lieferzettel erstellt' },
    { value: DeliveryStatusEnum.READYFORDELIVERY, label: 'Versandbereit' },
    { value: DeliveryStatusEnum.DELIVERYNOTECREATED, label: 'Lieferschein erstellt' },
    { value: DeliveryStatusEnum.DELIVERED, label: 'Ausgeliefert' },
  ];

  private subscriptions = new Subscription();

  constructor(
    private deliveryNoteService: DeliveryNoteService,
    private socketService: SocketService,
    private router: Router,
    public helperService: HelperService,
    private toastService: ToastService,
    private modalService: ModalService,
    private filterStorageService: FilterStorageService
  ) {}

  public ngOnInit(): void {
    this.socketService.connect();

    this.restoreStatusFilter();

    this.loadDeliveryNotes();

    this.subscriptions.add(
      this.searchTerms.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
        this.currentPage = 1;
        this.onSearch();
      })
    );

    // Subscribe to real-time updates
    this.subscriptions.add(
      this.socketService.deliveryNoteCreated$.subscribe(() => {
        this.loadDeliveryNotes();
      })
    );

    this.subscriptions.add(
      this.socketService.deliveryNoteUpdated$.subscribe(() => {
        this.loadDeliveryNotes();
      })
    );

    this.subscriptions.add(
      this.socketService.deliveryNoteDeleted$.subscribe(() => {
        this.loadDeliveryNotes();
      })
    );

    this.subscriptions.add(
      this.socketService.deliveryNoteStatusChanged$.subscribe(() => {
        this.loadDeliveryNotes();
      })
    );
  }

  public loadDeliveryNotes(): void {
    this.isLoading = true;
    const statusFilterValue = this.statusFilter?.length ? this.statusFilter.join(',') : '';
    this.subscriptions.add(
      this.deliveryNoteService
        .getDeliveryNotes(this.currentPage, this.pageSize, this.searchTerm, this.sortField, this.sortDirection, statusFilterValue)
        .subscribe({
          next: response => {
            if (response.DATA) {
              this.deliveryNotes = response.DATA.data;
              this.totalItems = response.DATA.total;
              this.totalPages = response.DATA.totalPages;
            }
            this.isLoading = false;
          },
          error: () => {
            console.error('Error loading delivery notes');
            this.isLoading = false;
          },
        })
    );
  }

  public onSearch(): void {
    this.currentPage = 1;
    this.loadDeliveryNotes();
  }

  public onPageChange(page: number): void {
    this.currentPage = page;
    this.loadDeliveryNotes();
  }

  public onSort(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
    this.loadDeliveryNotes();
  }

  public getSortIcon(field: string): string {
    return this.helperService.getSortIcon(this.sortField, this.sortDirection, field);
  }

  public getStatusName(status: DeliveryStatusEnum): string {
    return this.deliveryNoteService.getStatusName(status);
  }

  public getStatusClass(status: DeliveryStatusEnum): string {
    return this.deliveryNoteService.getStatusClass(status);
  }

  public getShippingTypeName(shippingType: string | undefined): string {
    if (shippingType === ShippingTypeEnum.PALETTEDELIVERY || shippingType === 'Palettenversand') {
      return 'Palettenversand';
    }

    if (shippingType === ShippingTypeEnum.PARCELDELIVERY || shippingType === 'Paketversand') {
      return 'Paketversand';
    }

    return shippingType || '—';
  }

  public isPaletteShippingWarning(deliveryNote: IDeliveryNote): boolean {
    const shippingType = String(deliveryNote.shippingType || '');
    return (
      (shippingType === ShippingTypeEnum.PALETTEDELIVERY || shippingType === 'Palettenversand') &&
      deliveryNote.status !== DeliveryStatusEnum.DELIVERED
    );
  }

  public getPositionsCount(deliveryNote: IDeliveryNote): number {
    if (!deliveryNote.positions?.length) return 0;
    const uniquePositions = new Set(deliveryNote.positions.map(position => (position?.articlePosition ?? '').toString().trim()));
    return uniquePositions.size;
  }

  public formatDate(date: Date | string | undefined): string {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  public clearSearch(): void {
    this.searchTerm = '';
    this.searchTerms.next('');
  }

  public onSearchChange(): void {
    this.searchTerms.next(this.searchTerm);
  }

  public openCreatePage(): void {
    this.router.navigate(['/delivery-notes/new']);
  }

  public openEditPage(deliveryNote: IDeliveryNote): void {
    this.router.navigate(['/delivery-notes/edit', deliveryNote._id]);
  }

  public onDeleteNote(deliveryNote: IDeliveryNote): void {
    if (!deliveryNote._id) return;

    this.modalService
      .open(DeleteConfirmationComponent, {
        title: 'Lieferzettel löschen',
        message: 'Möchten Sie diesen Lieferzettel wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      })
      .then((confirmed: boolean) => {
        if (!confirmed) return;
        this.subscriptions.add(
          this.deliveryNoteService.deleteDeliveryNote(deliveryNote._id!).subscribe({
            next: () => {
              this.toastService.showSuccess('Lieferzettel erfolgreich gelöscht');
              this.loadDeliveryNotes();
            },
            error: () => {
              console.error('Error deleting delivery note');
              this.toastService.showError('Fehler beim Löschen des Lieferzettels');
            },
          })
        );
      })
      .catch(() => {});
  }

  public onStatusFilterChange(): void {
    this.currentPage = 1;
    this.persistStatusFilter();
    this.loadDeliveryNotes();
  }

  private restoreStatusFilter(): void {
    this.statusFilter = this.filterStorageService.restore(
      EStorageKeys.DELIVERY_NOTE_STATUS_FILTER,
      this.statusOptions.map(o => o.value)
    );
  }

  private persistStatusFilter(): void {
    this.filterStorageService.persist(EStorageKeys.DELIVERY_NOTE_STATUS_FILTER, this.statusFilter || []);
  }

  public onQuickStatusChange(deliveryNote: IDeliveryNote, newStatus: DeliveryStatusEnum): void {
    if (!deliveryNote._id || deliveryNote.status === newStatus) return;

    if (newStatus === DeliveryStatusEnum.DELIVERED) {
      const allChecked = deliveryNote.positions?.every(p => p.deliveryCheckout?.checked === true);
      if (!allChecked) {
        this.toastService.showWarning('Bitte zuerst alle Positionen überprüfen');
        return;
      }
    }

    this.subscriptions.add(
      this.deliveryNoteService.updateDeliveryNoteStatus(deliveryNote._id, newStatus).subscribe({
        next: () => {
          // Update local state immediately for better UX
          deliveryNote.status = newStatus;
        },
        error: () => {
          console.error('Error updating delivery note status');
        },
      })
    );
  }

  public getNextStatus(currentStatus: DeliveryStatusEnum): DeliveryStatusEnum | null {
    return this.deliveryNoteService.getNextStatus(currentStatus);
  }

  public openOverviewModal(deliveryNote: IDeliveryNote): void {
    this.selectedDeliveryNote = deliveryNote;
    this.selectedOrderInfo = this.getOrderInfo(deliveryNote);
    this.showOverviewModal = true;
  }

  public closeOverviewModal(): void {
    this.showOverviewModal = false;
    this.selectedDeliveryNote = null;
    this.selectedOrderInfo = '';
  }

  public onEditFromModal(deliveryNote: IDeliveryNote): void {
    this.closeOverviewModal();
    this.openEditPage(deliveryNote);
  }

  public onNoteUpdated(updatedNote: IDeliveryNote): void {
    this.selectedDeliveryNote = updatedNote;
    const index = this.deliveryNotes.findIndex(n => n._id === updatedNote._id);
    if (index !== -1) {
      this.deliveryNotes[index] = updatedNote;
    }
  }

  public getOrderInfo(deliveryNote: IDeliveryNote): string {
    const order = deliveryNote.order;
    return order ? `${order.internalOrderId} - ${order.companyName}` : '';
  }

  ngOnDestroy(): void {
    this.persistStatusFilter();
    this.subscriptions.unsubscribe();
  }
}
